import {Observable, Subject} from 'rxjs';
import {SessionBehavior} from './session-behavior';

interface WebSocketMessageDict {
    login: { credentials: string; credentials_secret: string };
    login_accepted: { timestamp: string };
    login_rejected: { timestamp: string };
    session_started: { default_channel_id: string };
    heartbeat: { timestamp: string };
    channel_added: { channel_id: string; next_sequence_number: string };
    channel_removed: { channel_id: string };
    request_stream: {
        channel_id: string;
        begin_sequence_number: string;
        end_sequence_number: string;
        stream_id: string;
    };
    stream_started: { channel_id: string; begin_sequence_number: string };
    stream_ended: { channel_id: string };
    session_ended: { timestamp: string; session_id: string; reason: SessionEndReason };
    message_rejected: { text: string };
    stream_rejected: { timestamp: string; channel_id: string; stream_id: string; reason: StreamRejectReason };
    stream_resumed: { channel_id: string; next_sequence_number: string };
    logout: Record<PropertyKey, never>;
    start_session: Record<PropertyKey, never>;
}

const enum SessionEndReason {
    logout    = 'logout', // 1
    timeout   = 'timeout', // 2
    stopped   = 'stopped', // 3
    violation = 'violation' // 4
}

const enum StreamRejectReason {
    invalid_channel_id      = 'invalid_channel_id', // 1
    invalid_sequence_number = 'invalid_sequence_number', // 2
    stream_id_in_use        = 'stream_id_in_use', // 3
    too_many_streams        = 'too_many_streams' // 4
}

type WebSocketMessage = Partial<WebSocketMessageDict> & Record<string, any>;

type LoginMessage = Pick<WebSocketMessageDict, 'login'>;
type RequestStreamMessage = Pick<WebSocketMessageDict, 'request_stream'>;

interface OnMessageEvent {
    ses: Session;
    msg: WebSocketMessage;
}

interface OnCatchupEvent {
    ses: Session;
    percent: number;
}

interface OnErrorEvent {
    ses: Session;
    str: string;
}

/**
 * Main session class handling WebSocket communication
 */
export class Session {

    public static readonly bootstrapPathSuffix = '/bootstrap';
    public static readonly deltaPathSuffix = '/delta';

    private static readonly activeSessions = new Set<Session>();

    public closed: boolean;

    public readonly onMessage$: Observable<OnMessageEvent>;
    public readonly onOpen$: Observable<Session>;
    public readonly onCatchup$: Observable<OnCatchupEvent>;
    public readonly onClose$: Observable<Session>;
    public readonly onError$: Observable<OnErrorEvent>;

    private readonly credentials: string;
    private readonly credentialsSecret: string;

    private initialWs: WebSocket;
    private subsequentWs: WebSocket | null = null;
    private currentActiveWs: WebSocket;
    private readonly nextActiveWs: WebSocket;

    private readonly unusedChannels = new Set<bigint>();

    private lastHeartbeat = Date.now();

    private currentChannelId = -1n;
    private nextSequenceNumber = -1n;
    private upToDateChannelId = 0n;
    private upToDateNextSequenceNumber = 0n;
    private upToDatePercent = 0;

    private readonly onMessageSubject$ = new Subject<OnMessageEvent>();
    private readonly onOpenSubject$ = new Subject<Session>();
    private readonly onCatchupSubject$ = new Subject<OnCatchupEvent>();
    private readonly onCloseSubject$ = new Subject<Session>();
    private readonly onErrorSubject$ = new Subject<OnErrorEvent>();

    constructor(
        endpoint: string,
        credentials: string,
        credentialsSecret: string,
        behaviour?: SessionBehavior
        // heartbeatInterval: number = 15 // TODO
    ) {
        this.credentials = credentials;
        this.credentialsSecret = credentialsSecret;

        this.onMessage$ = this.onMessageSubject$.asObservable();
        this.onOpen$ = this.onOpenSubject$.asObservable();
        this.onCatchup$ = this.onCatchupSubject$.asObservable();
        this.onClose$ = this.onCloseSubject$.asObservable();
        this.onError$ = this.onErrorSubject$.asObservable();

        const behaviorInstance = behaviour || SessionBehavior.simple();

        const setupWs = (suffix: string): WebSocket => {
            const ws = new WebSocket(endpoint + suffix);

            ws.onopen = this.onWsOpen.bind(this);
            ws.onclose = this.onWsClose.bind(this);
            ws.onerror = this.onWsError.bind(this);
            ws.onmessage = this.onWsExpectNothing.bind(this);

            return ws;
        };

        this.initialWs = setupWs(behaviorInstance.initialSuffix);
        this.currentActiveWs = this.initialWs;

        if (behaviorInstance.subsequentSuffix) {
            this.subsequentWs = setupWs(behaviorInstance.subsequentSuffix);
            this.nextActiveWs = this.subsequentWs;
        } else {
            this.subsequentWs = null;
            this.nextActiveWs = this.initialWs;
        }
    }

    private static maybeHeartbeatAll(): void {
        Session.activeSessions.forEach(session => {
            session.maybeHeartbeat();
        });
    }

    public close(): void {
        this.internalClose();
    }

    public send(message: any): void {
        this.internalSend(this.currentActiveWs, message);
    }

    private log(str: string, message?: any): void {
        // TODO
        // if (!appConfig.production) {
        //     if (message === undefined) {
        //         console.log(str);
        //     } else {
        //         console.log(str + ': ' + this.encode(message));
        //     }
        // }
    }

    private encode<T>(message: T): string {
        return JSON.stringify(message);
    }

    private decode<T = WebSocketMessage>(data: string): T {
        return JSON.parse(data);
    }

    private internalSend<T = any>(ws: WebSocket, message: T): void {
        const data = this.encode(message);
        this.log('sending: ' + data);

        if (ws.readyState === WebSocket.OPEN) {
            ws.send(data);
        }
    }

    private internalClose(): void {
        if (this.initialWs) {
            this.log('closing session');
            Session.activeSessions.delete(this);
            this.initialWs.close();

            if (this.subsequentWs) {
                this.subsequentWs.close();
            }

            this.initialWs = null!;
            this.subsequentWs = null;
            this.onCloseSubject$.next(this);
        }

        this.closed = true;
    }

    private error(str: string): void {
        this.log('error: ' + str);
        this.internalClose();
        this.onErrorSubject$.next({ses: this, str});
    }

    private checkUpToDate(): void {
        if (this.upToDatePercent < 100) {
            let percent = this.upToDateNextSequenceNumber
                ? Math.round(Number(10000n * this.nextSequenceNumber / this.upToDateNextSequenceNumber) / 100)
                : 0;

            if (this.currentChannelId > this.upToDateChannelId) {
                percent = 100;
            } else if (this.currentChannelId === this.upToDateChannelId &&
                this.nextSequenceNumber >= this.upToDateNextSequenceNumber) {
                percent = 100;
            }

            if (percent !== this.upToDatePercent) {
                this.upToDatePercent = percent;
                this.onCatchupSubject$.next({ses: this, percent: this.upToDatePercent});
            }
        }
    }

    private login(ws: WebSocket): void {
        this.internalSend<LoginMessage>(ws, {
            login: {
                credentials: this.credentials,
                credentials_secret: this.credentialsSecret
            }
        });
    }

    private heartbeat(ws: WebSocket, now: number): void {
        this.internalSend(ws, {
            heartbeat: {
                timestamp: now * 1000000
            }
        });
    }

    private maybeHeartbeat(): void {
        const now = Date.now();

        if (now - this.lastHeartbeat > 1000) {
            this.lastHeartbeat = now;

            if (this.initialWs) {
                this.heartbeat(this.initialWs, now);
            }

            if (this.subsequentWs) {
                this.heartbeat(this.subsequentWs, now);
            }
        }
    }

    private requestStream(ws: WebSocket, channelId: bigint): void {
        this.internalSend<RequestStreamMessage>(ws, {
            request_stream: {
                channel_id:            channelId.toString(),
                begin_sequence_number: 1n.toString(),
                end_sequence_number:   'none',
                stream_id:             BigInt(Date.now()).toString()
            }
        });
    }

    private onWsOpen(evt: Event): void {
        (evt.target as WebSocket).onmessage = this.onWsExpectLoginReply.bind(this);
        this.login(evt.target as WebSocket);
    }

    private onWsClose(/*evt: CloseEvent*/): void {
        this.internalClose();
    }

    private onWsError(/*evt: Event*/): void {
        this.internalClose();
    }

    private onWsExpectNothing(/*evt: MessageEvent*/): void {
        this.error('unexpected reply');
    }

    private onWsExpectLoginReply(evt: MessageEvent<string>): void {
        const reply = this.decode(evt.data);
        this.log('received', reply);

        if (reply.login_accepted) {
            (evt.target as WebSocket).onmessage = this.onWsExpectSessionStarted.bind(this);
        } else if (reply.login_rejected) {
            this.error('login rejected');
        } else {
            this.error('unexpected reply');
        }
    }

    private onWsExpectSessionStarted(evt: MessageEvent<string>): void {
        const reply = this.decode(evt.data);
        this.log('received', reply);

        if (reply.session_started) {
            Session.activeSessions.add(this);

            if (evt.target === this.currentActiveWs) {
                (evt.target as WebSocket).onmessage = this.onWsExpectChannelAdded.bind(this);

                if (this.currentActiveWs === this.initialWs) {
                    this.upToDateChannelId = BigInt(reply.session_started.default_channel_id);

                    this.onOpenSubject$.next(this);
                }
            } else {
                (evt.target as WebSocket).onmessage = this.onWsExpectSessionMessagesOnly.bind(this);
            }
        } else {
            this.error('unexpected reply');
        }
    }

    private onWsExpectChannelAdded(evt: MessageEvent<string>): void {
        const reply = this.decode(evt.data);

        if (reply.channel_added) {
            this.log('adding channel ' + reply.channel_added.channel_id);
            this.unusedChannels.add(BigInt(reply.channel_added.channel_id));

            if (evt.target === this.initialWs &&
                BigInt(reply.channel_added.channel_id) === this.upToDateChannelId) {
                this.upToDateNextSequenceNumber = BigInt(reply.channel_added.next_sequence_number);
            }

            (evt.target as WebSocket).onmessage = this.onWsExpectStreamStarted.bind(this);
            this.requestStream(evt.target as WebSocket, BigInt(reply.channel_added.channel_id));
        } else if (reply.channel_removed) {
            Session.maybeHeartbeatAll();
        } else if (reply.heartbeat) {
            Session.maybeHeartbeatAll();
        } else if (reply.session_ended) {
            this.internalClose();
        } else {
            this.error('unexpected reply');
        }
    }

    private onWsExpectSessionMessagesOnly(evt: MessageEvent<string>): void {
        const reply = this.decode(evt.data);
        this.log('received', reply);

        if (reply.channel_added) {
            if (evt.target === this.nextActiveWs) {
                this.log('adding channel ' + reply.channel_added.channel_id);
                this.unusedChannels.add(BigInt(reply.channel_added.channel_id));

                if (evt.target === this.initialWs &&
                    BigInt(reply.channel_added.channel_id) === this.upToDateChannelId) {
                    this.upToDateNextSequenceNumber = BigInt(reply.channel_added.next_sequence_number);
                }
            }
            Session.maybeHeartbeatAll();
        } else if (reply.channel_removed) {
            Session.maybeHeartbeatAll();
        } else if (reply.heartbeat) {
            Session.maybeHeartbeatAll();
        } else if (reply.session_ended) {
            this.internalClose();
        } else {
            this.error('unexpected reply');
        }
    }

    private onWsExpectStreamStarted(evt: MessageEvent<string>): void {
        const reply = this.decode(evt.data);
        this.log('received', reply);

        if (reply.stream_started) {
            this.currentChannelId = BigInt(reply.stream_started.channel_id);
            this.nextSequenceNumber = BigInt(reply.stream_started.begin_sequence_number);
            this.checkUpToDate();
            (evt.target as WebSocket).onmessage = this.onWsExpectSessionAndStreamMessages.bind(this);
        } else if (reply.channel_added) {
            if (evt.target === this.nextActiveWs) {
                this.log('adding channel ' + reply.channel_added.channel_id);
                this.unusedChannels.add(BigInt(reply.channel_added.channel_id));

                if (evt.target === this.initialWs &&
                    BigInt(reply.channel_added.channel_id) === this.upToDateChannelId) {
                    this.upToDateNextSequenceNumber = BigInt(reply.channel_added.next_sequence_number);
                }
            }
            Session.maybeHeartbeatAll();
        } else if (reply.channel_removed) {
            Session.maybeHeartbeatAll();
        } else if (reply.heartbeat) {
            Session.maybeHeartbeatAll();
        } else if (reply.session_ended) {
            this.internalClose();
        } else {
            this.error('unexpected reply');
        }
    }

    private onWsExpectSessionAndStreamMessages(evt: MessageEvent<string>): void {
        const reply = this.decode(evt.data);
        this.log('received', reply);

        if (reply.channel_added) {
            if (evt.target === this.nextActiveWs) {
                this.log('adding channel ' + reply.channel_added.channel_id);
                this.unusedChannels.add(BigInt(reply.channel_added.channel_id));

                if (evt.target === this.initialWs &&
                    BigInt(reply.channel_added.channel_id) === this.upToDateChannelId) {
                    this.upToDateNextSequenceNumber = BigInt(reply.channel_added.next_sequence_number);
                }
            }
            Session.maybeHeartbeatAll();
        } else if (reply.channel_removed) {
            Session.maybeHeartbeatAll();
        } else if (reply.heartbeat) {
            Session.maybeHeartbeatAll();
        } else if (reply.stream_rejected) {
            this.error('stream request rejected');
        } else if (reply.stream_resumed) {
            this.currentChannelId = BigInt(reply.stream_resumed.channel_id);
            this.nextSequenceNumber = BigInt(reply.stream_resumed.next_sequence_number);
            this.checkUpToDate();
            Session.maybeHeartbeatAll();
        } else if (reply.stream_ended) {
            this.currentActiveWs = this.nextActiveWs;

            this.unusedChannels.delete(BigInt(reply.stream_ended.channel_id));

            if (this.unusedChannels.size > 0) {
                this.log('requesting next available channel');
                (this.currentActiveWs as WebSocket).onmessage = this.onWsExpectStreamStarted.bind(this);
                this.requestStream(this.currentActiveWs, this.unusedChannels.values().next().value);
            } else {
                this.log('waiting for next channel to become available');
                (this.currentActiveWs as WebSocket).onmessage = this.onWsExpectChannelAdded.bind(this);
            }
        } else if (reply.session_ended) {
            this.internalClose();
        } else if (reply.login || reply.logout || reply.login_accepted ||
            reply.login_rejected || reply.start_session || reply.session_started ||
            reply.request_stream || reply.stream_started) {
            this.error('unexpected reply');
        } else {
            if (evt.target === this.currentActiveWs) {
                ++this.nextSequenceNumber;
                this.checkUpToDate();
            }
            Session.maybeHeartbeatAll();

            this.onMessageSubject$.next({ses: this, msg: reply});
        }
    }
}
